import React, { useMemo } from 'react';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { coreCatalogApi } from '@cp/entities/catalog';
import { Map, MapProps, Marker } from 'src/shared/ui/map';
import { coreLocationApi } from '@cp/entities/location';
import { GeoPoint } from '@youhelp-ru/core';
import { Button } from '@cp/ds/src/components/button';
import { FullScreenExitIcon } from '@cp/ds/src/icons';
import { MAP_MARKER_CLICK_HANDLER_NAME, useMapMarkerClickHandler } from './hooks/useMapMarkerClickHandler';

export interface CatalogMapProps extends MapProps {
  listRequest: CatalogItemListRequest;
  onClose?: () => void;
}

export const CatalogMap: React.FC<CatalogMapProps> = ({ listRequest, onClose, ...mapProps }) => {
  const { data: catalogLocations } = coreCatalogApi.endpoints.getMapCatalogItems.useQuery({ ...listRequest, pagination: 10000 });
  const { data: city } = coreLocationApi.endpoints.getCityById.useQuery({ id: listRequest.cityId }, { skip: !listRequest.cityId });
  useMapMarkerClickHandler();

  const center = useMemo(() => {
    if (!city?.centerPoint) return;
    return 'geo:' + city.centerPoint;
  }, [city]);

  const markers = React.useMemo<Marker[] | undefined>(() => {
    if (!catalogLocations) {
      return;
    }

    return catalogLocations
      .map((catalogItem) => {
        const { id, location, clientProfile } = catalogItem;
        if (!location && clientProfile.location) return;
        return {
          id,
          location: location ? GeoPoint.parse('geo:' + location) : GeoPoint.parse('geo:' + clientProfile.location),
          onClick: (event) => {
            window[MAP_MARKER_CLICK_HANDLER_NAME](catalogItem, event);
          },
        };
      })
      .filter(Boolean) as NonNullable<Marker[]>;
  }, [catalogLocations]);

  return (
    <Map center={center} {...mapProps} markers={markers}>
      {onClose ? (
        <Button
          color="gray"
          onClick={onClose}
          size="small"
          square
          sx={{ backgroundColor: 'white', position: 'absolute', top: 20, right: 20, zIndex: 500 }}
          variant="secondary"
        >
          <FullScreenExitIcon />
        </Button>
      ) : null}
    </Map>
  );
};
